import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CurrencyPipe, DecimalPipe } from '@angular/common';

import { NzModalService } from 'ng-zorro-antd/modal';

import {
  AppOptionsService,
  ErrorsHandlingService,
  AuthService
} from '../../../../core/services';
import { AppOptions, HelpersInterface } from '../../../../core/interfaces';
import { TruncPipe } from '../../../../shared/pipes/trunc/trunc.pipe';
import { CompanyInterface, PartnerInterfaceGlobal } from '../../../../core/models';
import {
  ConnectionTypeEnum,
  UserRole
} from '../../../../core/enum';

import {
  EditSspDialogComponent,
  EditDspDialogComponent
} from '../../modals';

import { MainDashboardInterface } from '../../models';
import { MainService, MainTableService } from '../../services';

const slideInOut = trigger('slideInOut', [
  state('*', style({ height: '0px', display: 'none' })),
  state('true', style({ height: '*', display: 'block' })),
  state('false', style({ height: '0px', display: 'none' })),
  transition('* => true', animate('200ms ease-in')),
  transition('* => false', animate('200ms ease-out'))
]);

@Component({
  selector: 'app-list-endpoints',
  templateUrl: './list-endpoints.component.html',
  styleUrls: ['./list-endpoints.component.scss'],
  animations: [slideInOut],
  standalone: false
})
export class ListEndpointsComponent implements OnInit, AfterContentChecked {
  @Input() public isClosed: boolean;
  @Input() public userRole: UserRole;
  @Input() public endpoints: MainDashboardInterface.IEndpointsDataForMainTable[];
  @Input() public side: string;
  @Input() public companyId: number;
  @Input() public supply: boolean;
  @Input() public demand: boolean;
  @Input() public filterLists: HelpersInterface.IFilterListsItems[];
  @Input() public statesList: HelpersInterface.SelectItemHelper<string, string>[];
  @Input() public osList: HelpersInterface.SelectItemHelper<string | number, string>[];
  @Input() public allCompanies: CompanyInterface.ICompany[];
  @Input() public partnerSettings: PartnerInterfaceGlobal.IPartnerSettings | PartnerInterfaceGlobal.IPartnerGeneral;
  @Input() public rtbVersions: { id: string; name: string }[];
  @Output() public updateSSP = new EventEmitter<{ status: string; companyId: number; }>();
  @Output() public updateDSP = new EventEmitter<{ status: string; companyId: number; }>();
  @Output() public duplicatedEndpoint = new EventEmitter<{ endpointId: any; side: string; }>();
  @Output() public deleteEndpoint = new EventEmitter<{ endpointId: any; side: string; companyId?: number }>();
  @Output() public updateEndpointActiveStatus = new EventEmitter<{ side: string; type: string }>();

  @Input() public set schaninNodeLimitItems(value: { id: number; name: string }[]) {
    this._schaninNodeLimitItems = value;
  }

  public get schaninNodeLimitItems(): { id: number; name: string }[] {
    return this._schaninNodeLimitItems;
  }

  protected connectionTypes = ConnectionTypeEnum;
  private options: AppOptions;

  private _schaninNodeLimitItems: { id: number; name: string }[] = [];

  constructor(
    public truncPipe: TruncPipe,
    public currencyPipe: CurrencyPipe,
    public decimalPipe: DecimalPipe,
    private appOptionsService: AppOptionsService,
    private cd: ChangeDetectorRef,
    private modalService: NzModalService,
    private errorsHandlingService: ErrorsHandlingService,
    private mainService: MainService,
    private authService: AuthService,
    private mainTableService: MainTableService,
  ) { }

  public ngOnInit(): void {
    this.appOptionsService.currentOptions
      .subscribe((options: AppOptions) => {
        this.options = Object.assign({}, options);
      });
  }

  public ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  protected isDisabledSwitch(endpoint: MainDashboardInterface.IEndpointsDataForMainTable): boolean {
    const restrictionConnectionTypes = [4, 5];
    if (this.partnerSettings.partner_plan === 3 && restrictionConnectionTypes.includes(endpoint.connectionType)) {
      return true;
    } else {
      return this.userRole === UserRole.viewer || !!endpoint.deletedAt;
    }
  }

  protected isOptionActive(attr: string, side: string): boolean {
    return this.mainTableService.isOptionActiveHandler(attr, side, this.options);
  }

  protected onChangeActiveEndpoint(e: number, id: number, side: string) {
    const data = {
      active: Number(e)
    };

    this.mainService.updateEndpointById(id, data, side)
      .subscribe(() => {
        this.updateEndpointActiveStatus.emit({ side, type: 'companies' });
      },
        error => this.errorsHandlingService.handleError(error));
  }

  protected getMargin(endpoint: MainDashboardInterface.IEndpointsDataForMainTable): number | string | void {
    return this.mainTableService.getMarginByTypeHandler(endpoint);
  }

  protected openSspEditModal(endpointId: number, companyId: number) {
    const partnerSettings = this.authService.partner;
    const dialogRef = this.modalService.create({
      nzContent: EditSspDialogComponent,
      nzMaskClosable: false,
      nzClassName: 'endpoint-edit-modal',
      nzWrapClassName: 'endpoint-edit-modal-wrap',
      nzStyle: { top: '100px', width: '850px', overflow: 'hidden', },
      nzBodyStyle: { width: '850px', padding: '0', height: '580px', overflowY: 'scroll' },
      nzClosable: false,
      nzTitle: '',
      nzFooter: null,
      nzData: {
        endpointId: endpointId,
        companyId: companyId,
        partnerSettings: partnerSettings,
        companies: this.allCompanies,
        userRole: this.userRole,
        filterLists: this.filterLists,
      }
    });

    dialogRef.afterClose.subscribe(res => {
      const data = {
        status: res,
        companyId,
      };
      this.updateSSP.emit(data);
    });
  }
  protected openDspEditModal(endpointId: number, companyId: number) {
    const partnerSettings = this.authService.partner;
    const dialogRef = this.modalService.create({
      nzContent: EditDspDialogComponent,
      nzMaskClosable: false,
      nzClassName: 'endpoint-edit-dsp-modal',
      nzWrapClassName: 'endpoint-edit-dsp-modal-wrap',
      nzStyle: { top: '100px', width: '850px', overflow: 'hidden', },
      nzBodyStyle: { width: '850px', padding: '0', height: '580px', overflowY: 'scroll' },
      nzClosable: false,
      nzTitle: '',
      nzFooter: null,
      nzData: {
        endpointId: endpointId,
        companyId: companyId,
        partnerSettings: partnerSettings,
        companies: this.allCompanies,
        userRole: this.userRole,
        filterLists: this.filterLists,
        osList: this.osList,
        rtbVersions: this.rtbVersions,
        schaninNodeLimitItems: this.schaninNodeLimitItems,
        statesList: this.statesList,
      }
    });

    dialogRef.afterClose.subscribe(res => {
      const data = {
        status: res,
        companyId,
      };
      this.updateDSP.emit(data);
    });
  }
}
